/**
 * commerceBuilder — sub-container do slice commerce (Sprint F.5).
 *
 * Extraído de `container.ts` na Sprint F.5-polish-6 pra reduzir o monólito
 * de DI (~953L) por slice. Outros slices (crm/content/academico) podem ser
 * extraídos em etapas futuras.
 *
 * Convenção: builder recebe `httpClient` da raiz e retorna `{ gateways, useCases }`
 * que são spread no container final. Tipagem pública do `Container` não muda.
 *
 * @since F.5-polish-6
 */
import type { $Fetch } from 'ofetch';

import { HttpFinanceiroDashboardGateway } from '@/infrastructure/http/HttpFinanceiroDashboardGateway';
import { HttpPagamentoGateway } from '@/infrastructure/http/HttpPagamentoGateway';
import { HttpCobrancaReguaGateway } from '@/infrastructure/http/HttpCobrancaReguaGateway';
import { HttpRenegociacaoGateway } from '@/infrastructure/http/HttpRenegociacaoGateway';
import { HttpEstornoGateway } from '@/infrastructure/http/HttpEstornoGateway';
import { HttpVendaGateway } from '@/infrastructure/http/HttpVendaGateway';
import { HttpAdminParcelaGateway } from '@/infrastructure/http/HttpAdminParcelaGateway';
import { HttpAdminPagamentoGateway } from '@/infrastructure/http/HttpAdminPagamentoGateway';
import { HttpCobrancaEnvioGateway } from '@/infrastructure/http/HttpCobrancaEnvioGateway';
import { HttpCommissionRuleGateway } from '@/infrastructure/http/HttpCommissionRuleGateway';
import { HttpCommissionEntryGateway } from '@/infrastructure/http/HttpCommissionEntryGateway';
import { HttpPrecoGateway } from '@/infrastructure/http/HttpPrecoGateway';
import { HttpDescontoGateway } from '@/infrastructure/http/HttpDescontoGateway';
import { HttpWebhookSubscriptionGateway } from '@/infrastructure/http/HttpWebhookSubscriptionGateway';
import { HttpWebhookDeliveryGateway } from '@/infrastructure/http/HttpWebhookDeliveryGateway';
import { HttpMetaVendasGateway } from '@/infrastructure/http/HttpMetaVendasGateway';
import { HttpAlertaVendaGateway } from '@/infrastructure/http/HttpAlertaVendaGateway';
import { HttpTenantPagamentoConfigGateway } from '@/infrastructure/http/HttpTenantPagamentoConfigGateway';

import { GetFinanceiroDashboard } from '@/application/usecase/GetFinanceiroDashboard';
import {
  ListPagamentosByParcela, RegistrarPagamentoManual,
} from '@/application/usecase/PagamentoUseCaseImpl';
import {
  ListCobrancaReguaPassos, CreateCobrancaReguaPasso, UpdateCobrancaReguaPasso,
  DeleteCobrancaReguaPasso, RunReguaCobranca,
} from '@/application/usecase/CobrancaReguaUseCaseImpl';
import {
  ListRenegociacoes, SolicitarRenegociacao, DecidirRenegociacao,
} from '@/application/usecase/RenegociacaoUseCaseImpl';
import {
  ListEstornos, SolicitarEstorno, DecidirEstorno, ProcessarEstornoGateway,
} from '@/application/usecase/EstornoUseCaseImpl';
import {
  ListVendas, GetVenda, ListVendaParcelas, ListVendaDescontos, CancelarVenda,
} from '@/application/usecase/VendaUseCaseImpl';
import {
  ListAdminParcelas, ListAdminPagamentos, ListCobrancaEnvios, ListCobrancaEnviosByParcela,
} from '@/application/usecase/AdminFinanceiroUseCaseImpl';
import {
  ListCommissionRules, GetCommissionRule, CriarCommissionRule, AtualizarCommissionRule,
  DesativarCommissionRule, ListCommissionEntries, ApropriarCommissionEntry, PagarCommissionEntry,
} from '@/application/usecase/CommissionUseCaseImpl';
import {
  ListPrecos, GetPreco, CriarPreco, AtualizarPreco, InativarPreco,
  ListPrecoMeios, AddPrecoMeio, RemovePrecoMeio,
  ListDescontos, GetDesconto, CriarDesconto, AtivarDesconto, DesativarDesconto,
} from '@/application/usecase/PrecoDescontoUseCaseImpl';
import {
  ListWebhookSubscriptions, GetWebhookSubscription,
  CriarWebhookSubscription, AtualizarWebhookSubscription, DesativarWebhookSubscription,
  ListWebhookDeliveries, ReplayWebhookDelivery,
  ListMetasVendas, GetMetaVendas, CriarMetaVendas, AtualizarMetaVendas, DesativarMetaVendas,
  ListAlertasVenda, GetAlertaVenda, DeferirAlertaVenda, IndeferirAlertaVenda,
} from '@/application/usecase/Camada4UseCaseImpl';
import {
  GetTenantPagamentoConfig, UpsertTenantPagamentoConfig,
} from '@/application/usecase/TenantPagamentoConfigUseCaseImpl';

export function buildCommerceSlice(httpClient: $Fetch) {
  const financeiroDashboardGateway = new HttpFinanceiroDashboardGateway(httpClient);
  const pagamentoGateway = new HttpPagamentoGateway(httpClient);
  const cobrancaReguaGateway = new HttpCobrancaReguaGateway(httpClient);
  const renegociacaoGateway = new HttpRenegociacaoGateway(httpClient);
  const estornoGateway = new HttpEstornoGateway(httpClient);
  const vendaGateway = new HttpVendaGateway(httpClient);
  const adminParcelaGateway = new HttpAdminParcelaGateway(httpClient);
  const adminPagamentoGateway = new HttpAdminPagamentoGateway(httpClient);
  const cobrancaEnvioGateway = new HttpCobrancaEnvioGateway(httpClient);
  const commissionRuleGateway = new HttpCommissionRuleGateway(httpClient);
  const commissionEntryGateway = new HttpCommissionEntryGateway(httpClient);
  const precoGateway = new HttpPrecoGateway(httpClient);
  const descontoGateway = new HttpDescontoGateway(httpClient);
  const webhookSubscriptionGateway = new HttpWebhookSubscriptionGateway(httpClient);
  const webhookDeliveryGateway = new HttpWebhookDeliveryGateway(httpClient);
  const metaVendasGateway = new HttpMetaVendasGateway(httpClient);
  const alertaVendaGateway = new HttpAlertaVendaGateway(httpClient);
  const tenantPagamentoConfigGateway = new HttpTenantPagamentoConfigGateway(httpClient);

  const useCases = {
    getFinanceiroDashboard: new GetFinanceiroDashboard(financeiroDashboardGateway),
    listPagamentosByParcela: new ListPagamentosByParcela(pagamentoGateway),
    registrarPagamentoManual: new RegistrarPagamentoManual(pagamentoGateway),
    listCobrancaReguaPassos: new ListCobrancaReguaPassos(cobrancaReguaGateway),
    createCobrancaReguaPasso: new CreateCobrancaReguaPasso(cobrancaReguaGateway),
    updateCobrancaReguaPasso: new UpdateCobrancaReguaPasso(cobrancaReguaGateway),
    deleteCobrancaReguaPasso: new DeleteCobrancaReguaPasso(cobrancaReguaGateway),
    runReguaCobranca: new RunReguaCobranca(cobrancaReguaGateway),
    listRenegociacoes: new ListRenegociacoes(renegociacaoGateway),
    solicitarRenegociacao: new SolicitarRenegociacao(renegociacaoGateway),
    decidirRenegociacao: new DecidirRenegociacao(renegociacaoGateway),
    listEstornos: new ListEstornos(estornoGateway),
    solicitarEstorno: new SolicitarEstorno(estornoGateway),
    decidirEstorno: new DecidirEstorno(estornoGateway),
    processarEstornoGateway: new ProcessarEstornoGateway(estornoGateway),
    listVendas: new ListVendas(vendaGateway),
    getVenda: new GetVenda(vendaGateway),
    listVendaParcelas: new ListVendaParcelas(vendaGateway),
    listVendaDescontos: new ListVendaDescontos(vendaGateway),
    cancelarVenda: new CancelarVenda(vendaGateway),
    listAdminParcelas: new ListAdminParcelas(adminParcelaGateway),
    listAdminPagamentos: new ListAdminPagamentos(adminPagamentoGateway),
    listCobrancaEnvios: new ListCobrancaEnvios(cobrancaEnvioGateway),
    listCobrancaEnviosByParcela: new ListCobrancaEnviosByParcela(cobrancaEnvioGateway),
    listCommissionRules: new ListCommissionRules(commissionRuleGateway),
    getCommissionRule: new GetCommissionRule(commissionRuleGateway),
    criarCommissionRule: new CriarCommissionRule(commissionRuleGateway),
    atualizarCommissionRule: new AtualizarCommissionRule(commissionRuleGateway),
    desativarCommissionRule: new DesativarCommissionRule(commissionRuleGateway),
    listCommissionEntries: new ListCommissionEntries(commissionEntryGateway),
    apropriarCommissionEntry: new ApropriarCommissionEntry(commissionEntryGateway),
    pagarCommissionEntry: new PagarCommissionEntry(commissionEntryGateway),
    listPrecos: new ListPrecos(precoGateway),
    getPreco: new GetPreco(precoGateway),
    criarPreco: new CriarPreco(precoGateway),
    atualizarPreco: new AtualizarPreco(precoGateway),
    inativarPreco: new InativarPreco(precoGateway),
    listPrecoMeios: new ListPrecoMeios(precoGateway),
    addPrecoMeio: new AddPrecoMeio(precoGateway),
    removePrecoMeio: new RemovePrecoMeio(precoGateway),
    listDescontos: new ListDescontos(descontoGateway),
    getDesconto: new GetDesconto(descontoGateway),
    criarDesconto: new CriarDesconto(descontoGateway),
    ativarDesconto: new AtivarDesconto(descontoGateway),
    desativarDesconto: new DesativarDesconto(descontoGateway),
    listWebhookSubscriptions: new ListWebhookSubscriptions(webhookSubscriptionGateway),
    getWebhookSubscription: new GetWebhookSubscription(webhookSubscriptionGateway),
    criarWebhookSubscription: new CriarWebhookSubscription(webhookSubscriptionGateway),
    atualizarWebhookSubscription: new AtualizarWebhookSubscription(webhookSubscriptionGateway),
    desativarWebhookSubscription: new DesativarWebhookSubscription(webhookSubscriptionGateway),
    listWebhookDeliveries: new ListWebhookDeliveries(webhookDeliveryGateway),
    replayWebhookDelivery: new ReplayWebhookDelivery(webhookDeliveryGateway),
    listMetasVendas: new ListMetasVendas(metaVendasGateway),
    getMetaVendas: new GetMetaVendas(metaVendasGateway),
    criarMetaVendas: new CriarMetaVendas(metaVendasGateway),
    atualizarMetaVendas: new AtualizarMetaVendas(metaVendasGateway),
    desativarMetaVendas: new DesativarMetaVendas(metaVendasGateway),
    listAlertasVenda: new ListAlertasVenda(alertaVendaGateway),
    getAlertaVenda: new GetAlertaVenda(alertaVendaGateway),
    deferirAlertaVenda: new DeferirAlertaVenda(alertaVendaGateway),
    indeferirAlertaVenda: new IndeferirAlertaVenda(alertaVendaGateway),
    getTenantPagamentoConfig: new GetTenantPagamentoConfig(tenantPagamentoConfigGateway),
    upsertTenantPagamentoConfig: new UpsertTenantPagamentoConfig(tenantPagamentoConfigGateway),
  };

  return { useCases };
}
